// frontend/smartdocs-admin/src/components/chat/ChatWindow.tsx
import { useEffect, useRef } from 'react';
import ChatMessage from '@/components/chat/ChatMessage';
import ChatInput from '@/components/chat/ChatInput';
import api from "@/utils/api";
import useChatStore from '@/components/chat/store/chatStore'; // Import the Zustand store

const ChatWindow: React.FC = () => {
  // Use the Zustand store to manage messages
  const messages = useChatStore((state) => state.messages);
  const addMessage = useChatStore((state) => state.addMessage);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Function to scroll to the bottom of the chat window
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Scroll to bottom whenever messages update
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Function to send messages to the backend
  const handleSendMessage = async (text: string, direction: 'ltr' | 'rtl') => { // Added direction parameter
    if (text.trim() === '') return;

    const newUserMessage = {
      id: Date.now(), // Use Date.now() for a more robust unique ID
      text: text,
      direction: direction, // Use the direction passed from ChatInput
      isResponseTime: false,
      sender: 'user' as const,
      timestamp: new Date().toLocaleTimeString(),
    };
    addMessage(newUserMessage); // Add user message to the store

    try {
      const response = await api.sendMessage(text);

      if (!response.success) {
        throw new Error(`API error! message: ${response.message}`);
      }

      const newAiMessage = {
        id: Date.now() + 1, // Ensure unique ID for AI message
        text: response.answer,
        direction: (response.language === 'ar' ? 'rtl' : 'ltr') as 'ltr' | 'rtl',
        isResponseTime: true,
        sender: 'ai' as const,
        responseTime: response.responseTime,
        timestamp: new Date().toLocaleTimeString(),
      };

      addMessage(newAiMessage); // Add AI message to the store

    } catch (error: any) {
      console.error('Error sending message to backend:', error);
      // Add an error message to the chat if the API call fails
      addMessage({
        id: Date.now() + 2,
        text: `Oops! Something went wrong: ${error.message || 'Failed to get AI response.'}`,
        direction: 'ltr',
        isResponseTime: false,
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
        isError: true,
      });
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-b-xl shadow-md overflow-hidden">
      {/* Message Area */}
      <div className="flex-1 p-4 overflow-y-auto custom-scrollbar">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <span className="text-base text-wrap ">Hi there! 👋,<br/>
              I am your SmartDocs AI assistant.<br/>How can I serve you today?</span>
          </div>
        ) : (
          messages.map((msg) => (
            <ChatMessage key={msg.id} message={msg} />
          ))
        )}
        <div ref={messagesEndRef} /> {/* Scroll anchor */}
      </div>

      {/* chat Input */}
      <div className="p-4 border-t border-gray-200 bg-primary-50">
        <ChatInput onSendMessage={handleSendMessage} />
        <span className={`text-xs mt-2 block text-center`}>AI can make mistakes. Please double-check responses.</span>
      </div>
    </div>
  );
};

export default ChatWindow;
